import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';

import {Button, Card, Form, notification, Select} from 'antd';
import ResponsiveTable from "../../common/ResponsiveTable";

import {getDispensary} from '../actions';
import {bindActionCreators} from "redux";
import {getDescriptionFromError} from "../../utils/errors";
import {associateReportingType, getReportingTypeAssociations} from "./actions";
import {getRegulatoryReportingTypes} from "../../RegulatoryReporting/actions";
import {getRegulatoryBodyReportingCategories} from "../../RegulatoryBody/ReportingCategory/actions";
import {getRegulatoryBodies} from "../../RegulatoryBody/actions";
import {decodeParams} from "../../utils/params";

const Option = Select.Option;

class ReportingTypeAssociationsForm extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,

    loading: PropTypes.bool.isRequired,
    dispensary: PropTypes.object.isRequired,
    reportingTypeAssociations: PropTypes.array.isRequired,
    regulatoryReportingTypes: PropTypes.array.isRequired,
    regulatoryBodies: PropTypes.array.isRequired,
    regulatoryBodyReportingCategories: PropTypes.array.isRequired,

    getDispensary: PropTypes.func.isRequired,
    getRegulatoryReportingTypes: PropTypes.func.isRequired,
    getRegulatoryBodies: PropTypes.func.isRequired,
    getRegulatoryBodyReportingCategories: PropTypes.func.isRequired,
    getReportingTypeAssociations: PropTypes.func.isRequired,
    associateReportingType: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this._load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.dispensaryId !== this.props.match.params.dispensaryId) {
      this._load();
    }
  }

  _load = () => {
    const {dispensaryId} = decodeParams(this.props.match.params);
    if (dispensaryId) {
      this.props.getDispensary({dispensaryId});
      this.props.getRegulatoryReportingTypes();
      this.props.getRegulatoryBodies();
      this.props.getRegulatoryBodyReportingCategories({});
      this.props.getReportingTypeAssociations(dispensaryId);
    }
  };

  goBack = () => {
    this.props.history.goBack();
  };

  createChangeHandler(regulatoryReportingTypeId, regulatoryBody) {
    return (regulatoryBodyReportingCategoryId) => {
      const {regulatoryBodyId} = regulatoryBody;
      const {dispensaryId} = decodeParams(this.props.match.params);
      const args = {dispensaryId, regulatoryReportingTypeId, regulatoryBodyId, regulatoryBodyReportingCategoryId};
      this.props.associateReportingType(args)
        .then(() => {
          this.props.getReportingTypeAssociations();

          const reportingType = this.props.regulatoryReportingTypes.find(typ => typ.regulatoryReportingTypeId === regulatoryReportingTypeId);
          const reportingTypeName = (reportingType && reportingType.name) || "unknown type";
          let description;
          let message;
          if (regulatoryBodyReportingCategoryId) {
            const category = this.props.regulatoryBodyReportingCategories.find(cat => cat.regulatoryBodyReportingCategoryId === regulatoryBodyReportingCategoryId);
            const categoryName = (category && category.name) || "unknown category";
            const reportingUnit = (category && category.reportingUnit) || "?";
            description = `Now reporting ${reportingTypeName} to ${regulatoryBody.name} as ${categoryName} (${reportingUnit})`;
            message = 'Reporting Type Linked';
          } else {
            description = `No longer reporting ${reportingTypeName} to ${regulatoryBody.name}`;
            message = 'Reporting Type Disconnected';
          }
          notification.success({message, description});
        })
        .catch(err => notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        }));
    };
  }

  renderCell(regulatoryBody) {
    const {regulatoryBodyId} = regulatoryBody;
    return (regulatoryReportingTypeId) => {
      const currentAssoc = this.props.reportingTypeAssociations
        .find(assoc => assoc.regulatoryBodyId === regulatoryBodyId &&
          assoc.regulatoryReportingTypeId === regulatoryReportingTypeId);
      const currentId = currentAssoc ? currentAssoc.regulatoryBodyReportingCategoryId : null;
      const availableCategories = this.props.regulatoryBodyReportingCategories.filter(cat => cat.regulatoryBodyId === regulatoryBodyId);
      return (
        <Select onChange={this.createChangeHandler(regulatoryReportingTypeId, regulatoryBody)} value={currentId}>
          <Option key="categoryNullId" value={null}>NONE</Option>
          {availableCategories.map(({regulatoryBodyReportingCategoryId, name}) => <Option
            key={regulatoryBodyReportingCategoryId}
            value={regulatoryBodyReportingCategoryId}>{name}</Option>)}
        </Select>);
    };
  }

  render() {
    const {dispensary, loading, regulatoryReportingTypes, history, regulatoryBodies} = this.props;
    const title = `${dispensary.name} Regulatory Reporting`;
    const columns = [
      {
        title: 'Reporting Type',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
      },
    ];
    for (const regulatoryBody of regulatoryBodies) {
      columns.push({
        title: regulatoryBody.name,
        dataIndex: 'regulatoryReportingTypeId',
        key: regulatoryBody.regulatoryBodyId,
        render: this.renderCell(regulatoryBody),
      });
    }

    return (
      <Card title={[<Button key="back-button" icon="arrow-left" onClick={history.goBack}/>,
        ' ',
        title]}>
        <Form onSubmit={e => e.preventDefault()}>
          <ResponsiveTable
            loading={loading}
            columns={columns}
            dataSource={regulatoryReportingTypes}
            rowKey='regulatoryReportingTypeId'
            scroll={{x: this.props.regulatoryBodies.length * 200 + 50}}
            onChange={this._load}
          />
        </Form>
      </Card>
    );
  }
}

const ReportingTypeAssociationsPage = Form.create()(ReportingTypeAssociationsForm);

const mapStateToProps = state => ({
  loading:
    state.regulatoryReportingTypeAssociation.associatingReportType ||
    state.dispensary.dispensaryLoading ||
    state.regulatoryReportingType.regulatoryReportingTypesLoading ||
    state.regulatoryBody.regulatoryBodiesLoading ||
    state.regulatoryReportingTypeAssociation.reportingTypeAssociationsLoading ||
    state.regulatoryBodyReportingCategory.regulatoryBodyReportingCategoriesLoading,
  dispensary: state.dispensary.dispensary || {},
  regulatoryReportingTypes: state.regulatoryReportingType.regulatoryReportingTypes,
  regulatoryBodies: state.regulatoryBody.regulatoryBodies,
  regulatoryBodyReportingCategories: state.regulatoryBodyReportingCategory.regulatoryBodyReportingCategories,
  reportingTypeAssociations: state.regulatoryReportingTypeAssociation.reportingTypeAssociations,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  getDispensary,
  getReportingTypeAssociations,
  getRegulatoryReportingTypes,
  getRegulatoryBodies,
  getRegulatoryBodyReportingCategories,
  associateReportingType,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(ReportingTypeAssociationsPage);



// WEBPACK FOOTER //
// ./src/Dispensary/RegulatoryReporting/ReportingTypeAssociationsPage.js